import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Button,
} from "@/components/primitives";
import {
	Calendar,
	Lightbulb,
	BookOpen,
	TrendingUp,
	Brain,
	Target,
} from "lucide-react";

interface FirstTimeIntroProps {
	totalCheckInsCount: number;
	hasReflections: boolean;
	onCreateCheckIn: () => void;
	onCreateReflection: () => void;
}

export function FirstTimeIntro({
	totalCheckInsCount,
	hasReflections,
	onCreateCheckIn,
	onCreateReflection,
}: FirstTimeIntroProps) {
	return (
		<div className="max-w-4xl mx-auto space-y-8">
			<div className="text-center space-y-4">
				<h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
					{totalCheckInsCount === 0
						? "Welcome to Your Reflection Journal"
						: "About Daily Check-ins"}
				</h1>
				<p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
					{totalCheckInsCount === 0
						? "Before you start your first check-in, let's help you understand how this powerful tool works."
						: "Here's how daily check-ins work in your reflection journal."}
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-3">
						<Calendar className="h-6 w-6 text-blue-600" />
						What is a Daily Check-in?
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
						A daily check-in is a brief reflection where you record how you're
						feeling and what's happening in your life. It's like keeping a
						digital mood diary that helps you understand patterns in your
						emotions and experiences over time.
					</p>

					<div className="grid md:grid-cols-2 gap-6 mt-6">
						<div className="space-y-3">
							<h4 className="font-medium text-gray-900 dark:text-gray-100">
								What you'll do:
							</h4>
							<ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
								<li className="flex items-center gap-2">
									<div className="w-2 h-2 bg-blue-500 rounded-full" />
									Rate how you're feeling compared to your last check-in
								</li>
								<li className="flex items-center gap-2">
									<div className="w-2 h-2 bg-blue-500 rounded-full" />
									Describe what happened in your day
								</li>
								<li className="flex items-center gap-2">
									<div className="w-2 h-2 bg-blue-500 rounded-full" />
									Connect your experience to your reflections (if any)
								</li>
							</ul>
						</div>

						<div className="space-y-3">
							<h4 className="font-medium text-gray-900 dark:text-gray-100">
								What you'll gain:
							</h4>
							<ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
								<li className="flex items-center gap-2">
									<TrendingUp className="w-4 h-4 text-green-500" />
									Track emotional patterns over time
								</li>
								<li className="flex items-center gap-2">
									<Brain className="w-4 h-4 text-purple-500" />
									Insights about your wellbeing patterns
								</li>
								<li className="flex items-center gap-2">
									<Target className="w-4 h-4 text-orange-500" />
									Personalized reflection suggestions
								</li>
							</ul>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-3">
						<Lightbulb className="h-6 w-6 text-yellow-600" />
						Check-in Examples
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid md:grid-cols-2 gap-6">
						<div className="border rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
							<h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
								Good Day Example
							</h4>
							<p className="text-sm text-green-700 dark:text-green-300 mb-2">
								<strong>Mood:</strong> Slightly better than my last daily
								reflection
							</p>
							<p className="text-sm text-green-700 dark:text-green-300">
								<strong>Day:</strong> "Had a productive meeting at work and went
								for a walk during lunch. Feeling grateful for the sunny weather
								and good conversation with my colleague."
							</p>
						</div>

						<div className="border rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
							<h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">
								Challenging Day Example
							</h4>
							<p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
								<strong>Mood:</strong> Slightly worse than my last daily
								reflection
							</p>
							<p className="text-sm text-orange-700 dark:text-orange-300">
								<strong>Day:</strong> "Deadline pressure at work made me
								anxious. Didn't sleep well last night. Need to work on my time
								management and stress coping strategies."
							</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{totalCheckInsCount > 0 && (
				<div className="text-center">
					<Button onClick={onCreateCheckIn}>Create Check-in</Button>
				</div>
			)}

			{totalCheckInsCount === 0 && hasReflections && (
				<div className="text-center">
					<Button onClick={onCreateCheckIn}>
						<Calendar className="h-4 w-4 mr-2" />
						Continue to Check-in
					</Button>
				</div>
			)}

			{!hasReflections && (
				<Card className="border-2 border-dashed border-blue-200 dark:border-blue-800">
					<CardHeader>
						<CardTitle className="flex items-center gap-3">
							<BookOpen className="h-6 w-6 text-blue-600" />
							Connect with Your Reflections
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-gray-700 dark:text-gray-300">
							Check-ins become even more powerful when you have{" "}
							<strong>reflections</strong> to connect them to. Reflections are
							deeper explorations of specific topics like "Work-Life Balance" or
							"Managing Anxiety".
						</p>

						<div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
							<h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
								💡 Recommendation: Create a reflection first
							</h4>
							<p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
								To get maximum benefit from your check-ins, creating a
								reflection first helps you:
							</p>
							<ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-4">
								<li>
									• Focus your daily observations on specific growth areas
								</li>
								<li>• Track progress toward your personal goals</li>
								<li>• Get more targeted insights and suggestions</li>
							</ul>
						</div>

						<div className="flex flex-col sm:flex-row gap-3 pt-4">
							{totalCheckInsCount === 0 ? (
								<>
									<Button onClick={onCreateReflection} className="flex-1">
										<BookOpen className="h-4 w-4 mr-2" />
										Create Your First Reflection
									</Button>
									<Button onClick={onCreateCheckIn} className="flex-1">
										<Calendar className="h-4 w-4 mr-2" />
										Continue to Check-in
									</Button>
								</>
							) : (
								<Button onClick={onCreateReflection} className="w-full">
									<BookOpen className="h-4 w-4 mr-2" />
									Create a New Reflection
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
